// Demo scenario configurations for different use cases

export interface DemoMessage {
  id: string;
  user: string;
  userColor: string;
  avatar: string;
  time: string;
  text: string;
  reactions?: { emoji: string; count: number }[];
  isBot?: boolean;
}

export interface DemoThread {
  parentId: string;
  replies: DemoMessage[];
}

export interface DemoQueueItemConfig {
  id: string;
  text: string;
  addedBy: string;
  addedByName: string;
  assignedTo?: string;
  assignedToName?: string;
  botMessageId?: string;
  assignMessageId?: string;
}

export interface DemoScenario {
  channelName: string;
  channelEmoji: string;
  description: string;
  initialMessages: DemoMessage[];
  initialThreads: Record<string, DemoThread>;
  initialQueue: DemoQueueItemConfig[];
  initialMessageToQueueItem: Record<string, string>;
  responders: Array<{ name: string; avatar: string; color: string }>;
  asyncRequest?: { user: string; text: string };
}

const formatTime = (minutesAgo: number) => {
  const date = new Date(Date.now() - minutesAgo * 60 * 1000);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, "0");
  return `${displayHours}:${displayMinutes} ${ampm}`;
};

const HELP_TEXT = `*Kyubot Commands:*
• React 🎟️ to add, 🙋 to take, ✅ to complete
\`!add <item>\` - Add an item to the queue
\`!list\` - Show all items in the queue
\`!done\` - Mark first item as done
\`!done <n|text>\` - Mark specific item by number or matching text
\`!take\` - Assign current thread item to you`;

// IT Helpdesk scenario (original)
export const IT_HELPDESK: DemoScenario = {
  channelName: "it-helpdesk",
  channelEmoji: "💻",
  description: "IT Support",
  responders: [
    { name: "Neil", avatar: "/cat_with_glasses.webp", color: "text-blue-600" },
    { name: "Marcus", avatar: "/man_avatar_4.webp", color: "text-emerald-700" },
  ],
  asyncRequest: { user: "Neil", text: "Can someone help with printer access?" },
  initialMessages: [
    {
      id: "1",
      user: "Neil",
      userColor: "text-blue-600",
      avatar: "/cat_with_glasses.webp",
      time: formatTime(5),
      text: "Hey, I need help resetting my VPN credentials",
      reactions: [{ emoji: "🙋", count: 1 }],
      isBot: false,
    },
    {
      id: "2",
      user: "You",
      userColor: "text-zinc-600",
      avatar: "Y",
      time: formatTime(5),
      text: "!help",
      reactions: [],
      isBot: false,
    },
    {
      id: "3",
      user: "Kyubot",
      userColor: "text-orange-700",
      avatar: "/logo.webp",
      time: formatTime(5),
      text: HELP_TEXT,
      isBot: true,
    },
  ],
  initialThreads: {
    "1": {
      parentId: "1",
      replies: [
        {
          id: "thread-1-1",
          user: "Kyubot",
          userColor: "text-orange-700",
          avatar: "/logo.webp",
          time: formatTime(5),
          text: "Task added and assigned to Marcus",
          isBot: true,
        },
      ],
    },
  },
  initialQueue: [
    {
      id: "queue-1",
      text: "Hey, I need help resetting my VPN credentials",
      addedBy: "Neil",
      addedByName: "Neil",
      assignedTo: "Marcus",
      assignedToName: "Marcus",
      assignMessageId: "thread-1-1",
    },
  ],
  initialMessageToQueueItem: {
    "1": "queue-1",
  },
};

// Design Requests scenario
export const DESIGN_REQUESTS: DemoScenario = {
  channelName: "design-requests",
  channelEmoji: "🎨",
  description: "Design",
  responders: [
    { name: "Jordan", avatar: "/man_avatar_2.webp", color: "text-purple-600" },
    { name: "Casey", avatar: "/woman_avatar_2.webp", color: "text-emerald-700" },
  ],
  asyncRequest: { user: "Jordan", text: "Can someone mock up the new pricing page?" },
  initialMessages: [
    {
      id: "d1",
      user: "Jordan",
      userColor: "text-purple-600",
      avatar: "/man_avatar_2.webp",
      time: formatTime(8),
      text: "Can someone review the new dashboard mockups? Link in thread 👇",
      reactions: [{ emoji: "🎟️", count: 1 }],
      isBot: false,
    },
    {
      id: "d2",
      user: "Casey",
      userColor: "text-emerald-700",
      avatar: "/woman_avatar_2.webp",
      time: formatTime(6),
      text: "Need a hero image for the blog post about our Series A",
      reactions: [{ emoji: "🎟️", count: 1 }],
      isBot: false,
    },
    {
      id: "d3",
      user: "Kyubot",
      userColor: "text-orange-700",
      avatar: "/logo.webp",
      time: formatTime(6),
      text: `*Queue (2 items):*
1. Can someone review the new dashboard mockups? Link in thread 👇 | By: Jordan | 2m ago
2. Need a hero image for the blog post about our Series A | By: Casey | just now`,
      isBot: true,
    },
  ],
  initialThreads: {
    d1: {
      parentId: "d1",
      replies: [
        {
          id: "thread-d1-1",
          user: "Jordan",
          userColor: "text-purple-600",
          avatar: "/man_avatar_2.webp",
          time: formatTime(8),
          text: "Figma link: figma.com/file/dashboard-v2",
          isBot: false,
        },
        {
          id: "thread-d1-2",
          user: "Kyubot",
          userColor: "text-orange-700",
          avatar: "/logo.webp",
          time: formatTime(7),
          text: 'Added to queue: "Can someone review the new dashboard mockups?"',
          isBot: true,
        },
      ],
    },
    d2: {
      parentId: "d2",
      replies: [
        {
          id: "thread-d2-1",
          user: "Kyubot",
          userColor: "text-orange-700",
          avatar: "/logo.webp",
          time: formatTime(6),
          text: 'Added to queue: "Need a hero image for the blog post..."',
          isBot: true,
        },
      ],
    },
  },
  initialQueue: [
    {
      id: "queue-d1",
      text: "Can someone review the new dashboard mockups? Link in thread 👇",
      addedBy: "Jordan",
      addedByName: "Jordan",
      botMessageId: "thread-d1-2",
    },
    {
      id: "queue-d2",
      text: "Need a hero image for the blog post about our Series A",
      addedBy: "Casey",
      addedByName: "Casey",
      botMessageId: "thread-d2-1",
    },
  ],
  initialMessageToQueueItem: {
    d1: "queue-d1",
    d2: "queue-d2",
  },
};

// Bug Triage scenario
export const BUG_TRIAGE: DemoScenario = {
  channelName: "bug-triage",
  channelEmoji: "🐛",
  description: "Engineering",
  responders: [
    { name: "Sarah", avatar: "/woman_avatar.webp", color: "text-emerald-700" },
    { name: "Jayden", avatar: "/man_avatar.webp", color: "text-purple-600" },
  ],
  asyncRequest: { user: "Neil", text: "Can someone help with printer access?" },
  initialMessages: [
    {
      id: "b1",
      user: "Sarah",
      userColor: "text-emerald-700",
      avatar: "/woman_avatar.webp",
      time: formatTime(12),
      text: "🚨 Login button unresponsive on iOS Safari",
      reactions: [{ emoji: "🙋", count: 1 }],
      isBot: false,
    },
    {
      id: "b2",
      user: "Neil",
      userColor: "text-blue-600",
      avatar: "/cat_with_glasses.webp",
      time: formatTime(10),
      text: "Dashboard charts not loading for some users",
      reactions: [{ emoji: "🎟️", count: 1 }],
      isBot: false,
    },
    {
      id: "b3",
      user: "Jayden",
      userColor: "text-purple-600",
      avatar: "/man_avatar.webp",
      time: formatTime(3),
      text: "!list",
      isBot: false,
    },
    {
      id: "b4",
      user: "Kyubot",
      userColor: "text-orange-700",
      avatar: "/logo.webp",
      time: formatTime(3),
      text: `*Queue (2 items):*
1. 🚨 Login button unresponsive on iOS Safari | By: Sarah | Assigned: Jayden | 9m ago
2. Dashboard charts not loading for some users | By: Neil | 7m ago`,
      isBot: true,
    },
  ],
  initialThreads: {
    b1: {
      parentId: "b1",
      replies: [
        {
          id: "thread-b1-1",
          user: "Kyubot",
          userColor: "text-orange-700",
          avatar: "/logo.webp",
          time: formatTime(12),
          text: "Task added and assigned to Jayden",
          isBot: true,
        },
        {
          id: "thread-b1-2",
          user: "Jayden",
          userColor: "text-purple-600",
          avatar: "/man_avatar.webp",
          time: formatTime(8),
          text: "Looking into this now. Might be the iOS 17 update.",
          isBot: false,
        },
      ],
    },
    b2: {
      parentId: "b2",
      replies: [
        {
          id: "thread-b2-1",
          user: "Kyubot",
          userColor: "text-orange-700",
          avatar: "/logo.webp",
          time: formatTime(10),
          text: 'Added to queue: "Dashboard charts not loading for some users"',
          isBot: true,
        },
      ],
    },
  },
  initialQueue: [
    {
      id: "queue-b1",
      text: "🚨 Login button unresponsive on iOS Safari",
      addedBy: "Sarah",
      addedByName: "Sarah",
      assignedTo: "Jayden",
      assignedToName: "Jayden",
      assignMessageId: "thread-b1-1",
    },
    {
      id: "queue-b2",
      text: "Dashboard charts not loading for some users",
      addedBy: "Neil",
      addedByName: "Neil",
      botMessageId: "thread-b2-1",
    },
  ],
  initialMessageToQueueItem: {
    b1: "queue-b1",
    b2: "queue-b2",
  },
};

// Code Review scenario
export const CODE_REVIEW: DemoScenario = {
  channelName: "code-review",
  channelEmoji: "🔍",
  description: "Engineering",
  responders: [
    { name: "Sarah", avatar: "/woman_avatar.webp", color: "text-emerald-700" },
    { name: "Jayden", avatar: "/man_avatar.webp", color: "text-purple-600" },
  ],
  asyncRequest: { user: "Neil", text: "Anyone available to review PR #312?" },
  initialMessages: [
    {
      id: "r1",
      user: "Jayden",
      userColor: "text-purple-600",
      avatar: "/man_avatar.webp",
      time: formatTime(5),
      text: "PR ready for review: Add rate limiting to API endpoints",
      reactions: [{ emoji: "🙋", count: 1 }],
      isBot: false,
    },
    {
      id: "r2",
      user: "You",
      userColor: "text-zinc-600",
      avatar: "Y",
      time: formatTime(5),
      text: "!help",
      reactions: [],
      isBot: false,
    },
    {
      id: "r3",
      user: "Kyubot",
      userColor: "text-orange-700",
      avatar: "/logo.webp",
      time: formatTime(5),
      text: HELP_TEXT,
      isBot: true,
    },
  ],
  initialThreads: {
    r1: {
      parentId: "r1",
      replies: [
        {
          id: "thread-r1-1",
          user: "Kyubot",
          userColor: "text-orange-700",
          avatar: "/logo.webp",
          time: formatTime(5),
          text: "Task added and assigned to Sarah",
          isBot: true,
        },
      ],
    },
  },
  initialQueue: [
    {
      id: "queue-r1",
      text: "PR ready for review: Add rate limiting to API endpoints",
      addedBy: "Jayden",
      addedByName: "Jayden",
      assignedTo: "Sarah",
      assignedToName: "Sarah",
      assignMessageId: "thread-r1-1",
    },
  ],
  initialMessageToQueueItem: {
    r1: "queue-r1",
  },
};

// Customer Success scenario
export const CUSTOMER_SUCCESS: DemoScenario = {
  channelName: "customer-success",
  channelEmoji: "🤝",
  description: "Success",
  responders: [
    { name: "Robert", avatar: "/man_avatar_3.webp", color: "text-emerald-700" },
    { name: "Morgan", avatar: "/woman_avatar_3.webp", color: "text-purple-600" },
  ],
  asyncRequest: { user: "Robert", text: "New enterprise lead just came in, anyone free?" },
  initialMessages: [
    {
      id: "c1",
      user: "Robert",
      userColor: "text-emerald-700",
      avatar: "/man_avatar_3.webp",
      time: formatTime(15),
      text: "Acme Corp needs help with their API integration - they're on Enterprise plan",
      reactions: [{ emoji: "🎟️", count: 1 }],
      isBot: false,
    },
    {
      id: "c2",
      user: "Morgan",
      userColor: "text-purple-600",
      avatar: "/woman_avatar_3.webp",
      time: formatTime(10),
      text: "New trial user asking about bulk import feature",
      reactions: [{ emoji: "🙋", count: 1 }],
      isBot: false,
    },
    {
      id: "c3",
      user: "Kyubot",
      userColor: "text-orange-700",
      avatar: "/logo.webp",
      time: formatTime(10),
      text: `*Queue (2 items):*
1. Acme Corp needs help with their API integration - they're on Enterprise plan | By: Robert | 5m ago
2. New trial user asking about bulk import feature | By: Morgan | Assigned: You | just now`,
      isBot: true,
    },
  ],
  initialThreads: {
    c1: {
      parentId: "c1",
      replies: [
        {
          id: "thread-c1-1",
          user: "Kyubot",
          userColor: "text-orange-700",
          avatar: "/logo.webp",
          time: formatTime(15),
          text: 'Added to queue: "Acme Corp needs help with their API integration..."',
          isBot: true,
        },
        {
          id: "thread-c1-2",
          user: "Robert",
          userColor: "text-emerald-700",
          avatar: "/man_avatar_3.webp",
          time: formatTime(14),
          text: "Their contact is john@acmecorp.com, they're in PST timezone",
          isBot: false,
        },
      ],
    },
    c2: {
      parentId: "c2",
      replies: [
        {
          id: "thread-c2-1",
          user: "Kyubot",
          userColor: "text-orange-700",
          avatar: "/logo.webp",
          time: formatTime(10),
          text: "Task added and assigned to You",
          isBot: true,
        },
      ],
    },
  },
  initialQueue: [
    {
      id: "queue-c1",
      text: "Acme Corp needs help with their API integration - they're on Enterprise plan",
      addedBy: "Robert",
      addedByName: "Robert",
      botMessageId: "thread-c1-1",
    },
    {
      id: "queue-c2",
      text: "New trial user asking about bulk import feature",
      addedBy: "Morgan",
      addedByName: "Morgan",
      assignedTo: "You",
      assignedToName: "You",
      assignMessageId: "thread-c2-1",
    },
  ],
  initialMessageToQueueItem: {
    c1: "queue-c1",
    c2: "queue-c2",
  },
};

export const ALL_SCENARIOS: Record<string, DemoScenario> = {
  "bug-triage": BUG_TRIAGE,
  "code-review": CODE_REVIEW,
  "it-helpdesk": IT_HELPDESK,
  "design-requests": DESIGN_REQUESTS,
  "customer-success": CUSTOMER_SUCCESS,
};

export const CHANNEL_ORDER = [
  "code-review",
  "bug-triage",
  "it-helpdesk",
  "design-requests",
  "customer-success",
];
